import {
  pendingPaymentAtom,
  selectedPaymentMethodAtom,
  paymentMethodOnFileAtom,
  contractIdAtom,
} from '@openeducation/pp-rn-jotai-atoms';
import {
  PaymentMethod,
  PaymentGateway,
  ContractParams,
  PaymentSubmitProps,
  PaymentMethodOnFile,
  ManualPaymentParams,
  SubmitPaymentReturn,
  BillingAddress,
  BillingAgreements,
  AlternativePaymentInfo,
  ExtendedPaymentInfoFormData,
} from '@openeducation/pp-rn-shared-types';
import { useAtomValue } from 'jotai';

import { useHandlePaymentErrors } from './use-handle-payment-errors';
import { useHandlePaymentSubmit } from './use-handle-payment-submit';
import { useAcceptContract } from '../../../../hooks';
import { getPendingPaymentMethod } from '../../../helpers';
import { useSubmitPayU, SubmitPayUParams } from '../../use-submit-payu';
import { useSubmitYuno, SubmitYunoParams } from '../../use-submit-yuno';
import { SubmitPayPalParams, useSubmitPayPal } from '../use-submit-pay-pal';

interface UseSubmitPayment {
  paymentInfo: ExtendedPaymentInfoFormData;
  billingAddress: BillingAddress;
  billingAddress2?: BillingAddress;
  sfdcToken?: string;
  isSubmitEnabled: boolean;
  documentNumber?: string;
  documentNumber2?: string;
  documentType?: string;
  documentType2?: string;
  isUpsell?: boolean;
  contractParams: ContractParams | null;
  submitPayPalParams?: SubmitPayPalParams;
  submitPayUParams?: SubmitPayUParams;
  submitYunoParams?: SubmitYunoParams;
  manualPaymentParams?: ManualPaymentParams;
  submitPaymentReturn: SubmitPaymentReturn;
  isScreenLoading?: boolean;
  billingAgreements?: BillingAgreements;
  validateAddress: () => void;
  validateAddress2?: () => void;
  isAddressValid: boolean;
  isAddressValid2?: boolean;
  alternativePaymentInfo?: AlternativePaymentInfo;
}

export const usePaymentSubmitProps = ({
  alternativePaymentInfo,
  paymentInfo,
  documentNumber,
  documentNumber2,
  documentType,
  documentType2,
  isUpsell,
  contractParams,
  sfdcToken,
  isSubmitEnabled,
  submitPayPalParams = {} as SubmitPayPalParams,
  submitPayUParams = {} as SubmitPayUParams,
  submitYunoParams = {} as SubmitYunoParams,
  billingAddress,
  billingAddress2,
  manualPaymentParams,
  submitPaymentReturn,
  isScreenLoading,
  billingAgreements,
  validateAddress,
  validateAddress2,
  isAddressValid,
  isAddressValid2,
}: UseSubmitPayment) => {
  const { paymentMethod } = paymentInfo;
  const pendingPayment = useAtomValue(pendingPaymentAtom);
  const selectedPaymentMethod = useAtomValue(selectedPaymentMethodAtom);
  const selectedPaymentMethodOnFile = useAtomValue(paymentMethodOnFileAtom);
  const atomContractId = useAtomValue(contractIdAtom) as string;

  const {
    data: paymentResponse,
    submitPayment,
    isError: isSubmitError,
    apiError: submitApiError,
    submitPaymentReset,
  } = submitPaymentReturn;

  const isPayPal = selectedPaymentMethod === PaymentMethod.PAY_PAL;
  const isPayPalRedirectionFlow =
    isPayPal && selectedPaymentMethodOnFile !== PaymentMethodOnFile.EXISTING;

  const alternativePaymentGateway =
    alternativePaymentInfo?.paymentGateway || PaymentGateway.PAYU;

  const isPayU =
    paymentMethod === PaymentMethod.ALTERNATIVE &&
    alternativePaymentGateway !== PaymentGateway.YUNO;

  const isYuno =
    paymentMethod === PaymentMethod.ALTERNATIVE &&
    alternativePaymentGateway === PaymentGateway.YUNO;

  const {
    data: newContractId = '',
    acceptContract,
    isLoading: isAcceptLoading,
    isError: isAcceptError,
    acceptContractReset,
  } = useAcceptContract({
    contract: contractParams,
  });
  const contractId = isAcceptLoading ? '' : atomContractId || newContractId;
  const contractAccepted = !isAcceptLoading && !isAcceptError && !!contractId;

  const { submitPayPal, payPalApiError, isPayPalError, payPalReset } =
    useSubmitPayPal(submitPayPalParams, newContractId);

  const { submitPayU } = useSubmitPayU(submitPayUParams);

  const { submitYuno, yunoApiError, isYunoError, yunoReset } =
    useSubmitYuno(submitYunoParams);

  useHandlePaymentErrors({
    submitPaymentReset,
    acceptContractReset,
    payPalReset,
    isAcceptError,
    isSubmitError,
    isPayPalError,
    submitApiError,
    payPalApiError,
    yunoApiError,
    isYunoError,
    yunoReset,
  });

  const isTwoCreditCards = paymentMethod === PaymentMethod.TWO_CREDIT_CARDS;

  const pendingPaymentMethod = getPendingPaymentMethod(
    isPayPalRedirectionFlow,
    isPayU,
    isYuno,
    isTwoCreditCards
  );

  const { onSubmitButtonPress } = useHandlePaymentSubmit({
    alternativePaymentGateway,
    submitPaymentReset,
    submitPayU,
    acceptContract: contractParams ? acceptContract : undefined,
    submitPayPal,
    isYuno,
    submitYuno,
    submitPayment,
    paymentResponse,
    pendingPaymentMethod,
    paymentInfo,
    billingAddress,
    billingAddress2,
    manualPaymentParams,
    isPayU,
    isSubmitError,
    isPayPalRedirectionFlow,
    isUpsell,
    contractAccepted,
    validateAddress,
    validateAddress2,
    isAddressValid,
    isAddressValid2,
    sfdcToken,
    contractId,
    documentNumber,
    documentNumber2,
    documentType,
    documentType2,
    isScreenLoading,
    billingAgreements,
  });

  const paymentSubmitProps: PaymentSubmitProps = {
    submitPayment: onSubmitButtonPress,
    isLoading: pendingPayment !== null,
    isSubmitEnabled,
    isPayPal,
    isPayU,
    isYuno,
  };

  return { paymentSubmitProps };
};
